import * as THREE from 'three';

// Import types for post-processing effects
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

// Import helpers and utilities
import { RectAreaLightUniformsLib } from 'three/examples/jsm/lights/RectAreaLightUniformsLib.js';
import CameraControls from 'camera-controls';
import { throttle } from 'lodash';

// Import application-specific modules and types
import store from '@/store';
import { ThreeViewerParams } from '@/types/threeViever';
import getPointerPosition from '@/components/ProductProfile/utils/getPointerPosition';
import disposeThreeObject from '../utils/disposeThreeObject';

// Install CameraControls to extend THREE
CameraControls.install({ THREE: THREE });

// Reusable Three.js objects to avoid creating new ones in the render loop
const clock = new THREE.Clock();
const reusableEuler = new THREE.Euler();
const reusableMatrix4 = new THREE.Matrix4();
const mouseVector2Reusable = new THREE.Vector2();
const mouseDownVector2Reusable = new THREE.Vector2();



class ThreeViewer {
	// --- PROPERTIES ---

	// Core properties from constructor
	public readonly params: ThreeViewerParams;
	public readonly element: HTMLElement;

	// Dimensions
	public clientWidth: number;
	public clientHeight: number;

	// Core Three.js components
	public scene!: THREE.Scene;
	public camera!: THREE.PerspectiveCamera;
	public renderer!: THREE.WebGLRenderer;
	public controls!: CameraControls;

	// Post-processing
	public composer!: EffectComposer;
	public fxaa!: ShaderPass;

	// Lights
	public keyLight: THREE.RectAreaLight | null = null;
	public fillLight: THREE.RectAreaLight | null = null;

	// Interaction state
	public mouse: THREE.Vector2;
	public downMouse: THREE.Vector2;
	public intersection: Record<string, any> = {};
	public isPinching: boolean = false;
	public touchCount: number = 0;
	public controlStartByUser: boolean = false;

	// Application state and data
	public selectedSection: any = null;
	public selectedElement: any = null;
	public handlerData: Record<string, any> = {};

	// Scene objects
	public haveToCleanFromScene: THREE.Object3D[] = [];

	// Timers and Animation Control
	private animationFrameId: number | null = null;
	private setPixelRatioTimer: ReturnType<typeof setTimeout> | null = null;
	public fullScreen: boolean = false;
	public handlerBtnUpdateSize: (() => void) | null = null;

	// Throttled event handlers
	private readonly throttledUpdateSize: () => void;
	private backgroundColor: string;

	private activeGetTargetObject: boolean = false;
	private outlinePass: OutlinePass | null = null;

	public overlyTint = new THREE.Color().setHSL(0, 0, 0.5);
	public overlayScene!: THREE.Scene;

	private hovered: THREE.Object3D | null = null;
	private _resolvePick?: (obj: THREE.Object3D | null) => void;
	/**
	 * Where on the surface the hover is, not just which object.
	 *
	 * The cylindrical model only ever needed the mesh, because a section is then placed by
	 * an angle typed into the form. A frame is placed by the point itself and the direction
	 * the surface faces there, so the pick has to keep what the raycast already computed and
	 * currently throws away. Recorded beside `hovered` rather than replacing it, so the
	 * existing pick contract is untouched.
	 */
	private hoveredHit: { object: THREE.Object3D; point: THREE.Vector3; normal: THREE.Vector3 } | null = null;
	private lastPickHit: { object: THREE.Object3D; point: THREE.Vector3; normal: THREE.Vector3 } | null = null;

	constructor(params: ThreeViewerParams) {
		this.params = params;
		this.element = params.element;

		this.backgroundColor = params.bgColor || '0xffffff';

		this.clientWidth = params.element.clientWidth;
		this.clientHeight = params.element.clientHeight;

		this.mouse = mouseVector2Reusable;
		this.downMouse = mouseDownVector2Reusable;

		this.throttledUpdateSize = throttle(() => this.updateSize(), 200);
		this.cleanScene = this.cleanScene.bind(this);
		this.updatePixelRatio = this.updatePixelRatio.bind(this);
		this.updateSize = this.updateSize.bind(this);

		(window as any).setPixelRatio = 3;
	}

	public cleanScene(): void {
		if (this.haveToCleanFromScene?.length > 0) {
			this.haveToCleanFromScene.forEach((object) => {
				disposeThreeObject(object);
				this.scene.remove(object);
			});
			this.haveToCleanFromScene = [];
			this.renderer.renderLists.dispose();
		}
	}

	private initCamera(): void {
		if (!this.camera) {
			this.camera = new THREE.PerspectiveCamera(20, this.clientWidth / this.clientHeight, 0.01, 100);
			this.camera.position.set(0, 0, 0.5);
			this.camera.layers.enableAll();
			this.scene.add(this.camera);
		}
	}

	private initLights(): void {
		this.keyLight = new THREE.RectAreaLight(0xfffffa, 0.05, 2, 50);
		this.keyLight.position.set(-1, 10, 1);
		this.scene.add(this.keyLight);

		this.fillLight = new THREE.RectAreaLight(0xfffffa, 0.05, 2, 50);
		this.fillLight.position.set(-1, 10, 1);
		this.scene.add(this.fillLight);
	}

	public updateLightPosition(): void {
		if (!this.camera) return;

		const quaternion = this.camera.quaternion;
		reusableEuler.setFromQuaternion(quaternion, 'YXZ');

		// The turntable counter-rotation, for cylindrical profiles only.
		//
		// Spinning the SCENE by the camera's own yaw makes the product appear to turn while
		// the lighting stays put, which is the look a tumbler wants. But it also slaves the
		// camera's world azimuth to a fixed value: aim at a point and the loop rotates the
		// scene until it settles, so the point ends up centred but seen from whatever angle
		// the loop chose. Simulated on a side-facing section, that lands the camera 141.6
		// degrees off the section's normal — centred, and looking at it from behind.
		//
		// A frame section has a normal and the whole point is to face it, so frame profiles
		// keep the scene still. That also leaves the camera's parent at identity, which is
		// what camera-controls assumes anyway — see shared/sectionFrames/cameraAim.
		if ((window as any).threeViewer?.sectionModel === 'frame') {
			this.scene.rotation.y = 0;
		} else {
			this.scene.rotation.y = -reusableEuler.y + ((window as any).leftP || 1);
		}

		const rotationMatrix = reusableMatrix4.makeRotationFromEuler(reusableEuler);

		if (this.keyLight) {
			const initialPosition = new THREE.Vector3(3, 1, 5);
			const rotatedPosition = initialPosition.applyMatrix4(rotationMatrix);
			this.keyLight.position.copy(rotatedPosition);
			this.keyLight.lookAt(2, 4, 2);
			if ((window as any).keyLight) this.keyLight.intensity = (window as any).keyLight;
		}

		if (this.fillLight) {
			const initialPosition = new THREE.Vector3(-4, 1, 5);
			const rotatedPosition = initialPosition.applyMatrix4(rotationMatrix);
			this.fillLight.position.copy(rotatedPosition);
			this.fillLight.lookAt(-3, 4, 2);
			if ((window as any).fillLight) this.fillLight.intensity = (window as any).fillLight;
		}

		if ((window as any).toneMappingExposure) this.renderer.toneMappingExposure = (window as any).toneMappingExposure;
	}

	private onClick = () => {
		if (!this.activeGetTargetObject) return;
		this.finishPick(this.hovered || null);
	};

	private finishPick(obj: THREE.Object3D | null) {
		this.lastPickHit = this.hoveredHit;
		this.activeGetTargetObject = false;
		if (this.outlinePass) this.outlinePass.selectedObjects = [];
		const r = this._resolvePick;
		this._resolvePick = undefined;
		r?.(obj);
	}

	/**
	 * Surface detail from the most recent pick, for callers that place a frame.
	 *
	 * Null when the pick landed on nothing that faced the camera — which happens on a
	 * double-sided model where the ray entered through a gap and only hit interior walls.
	 * A frame placed there would face into the part and never show, so returning null is
	 * the honest answer rather than a point on the far side.
	 */
	public getLastPickHit(): { object: THREE.Object3D; point: THREE.Vector3; normal: THREE.Vector3 } | null {
		return this.lastPickHit;
	}

	public getTargetObject(): Promise<THREE.Object3D | null> {
		this.activeGetTargetObject = true;
		return new Promise(res => { this._resolvePick = res; });
	}

	public initViewer(): void {
		RectAreaLightUniformsLib.init();

		this.scene = new THREE.Scene();
		this.initCamera();
		this.initLights();

		this.renderer = new THREE.WebGLRenderer({
			antialias: true,
			alpha: false,
			powerPreference: 'high-performance',
			premultipliedAlpha: false,
			precision: 'highp',
		});
		this.renderer.setClearColor(this.backgroundColor as unknown as number, 1);
		this.renderer.setPixelRatio((window as any).setPixelRatio);
		this.renderer.outputColorSpace = THREE.SRGBColorSpace;
		this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
		this.renderer.toneMappingExposure = 1;

		this.composer = new EffectComposer(this.renderer);
		this.composer.addPass(new RenderPass(this.scene, this.camera));

		this.fxaa = new ShaderPass(FXAAShader);
		const r = this.renderer.getPixelRatio();
		this.fxaa.material.uniforms['resolution'].value.set(1 / ((window as any).innerWidth * r), 1 / ((window as any).innerHeight * r));
		this.composer.addPass(this.fxaa);

		this.overlayScene = new THREE.Scene();

		this.renderer.autoClear = false;

		this.setUpCameraControls();

		this.element.appendChild(this.renderer.domElement);

		// single resize listener
		(window as any).addEventListener('resize', this.throttledUpdateSize, false);

		this.controls.addEventListener('update', () => {
			const quaternion = this.camera.quaternion.clone();
			(window as any).controlY = quaternion.y;
			this.handlerBtnUpdateSize?.();
			this.updateLightPosition();
			this.updatePixelRatio(true);
		});

		const raycaster = new THREE.Raycaster();
		const mouse = new THREE.Vector2();

		const { clientWidth, clientHeight } = this.element;
		this.outlinePass = new OutlinePass(
			new THREE.Vector2(clientWidth, clientHeight),
			this.scene,
			this.camera
		);
		this.outlinePass.edgeThickness = 7;
		this.outlinePass.edgeStrength = 10;
		this.outlinePass.edgeGlow = 0;
		this.outlinePass.pulsePeriod = 0;
		this.outlinePass.usePatternTexture = false;
		this.outlinePass.visibleEdgeColor.set('#ffe573');
		this.outlinePass.hiddenEdgeColor.set('#ffe573');
		this.outlinePass.overlayMaterial.blending = THREE.NormalBlending;
		this.composer.addPass(this.outlinePass);

		this.renderer.autoClear = false;

		this.renderer.domElement.addEventListener('pointermove', (e) => {
			const position = getPointerPosition(e);
			if (!position || !this.activeGetTargetObject) {
				if (this.outlinePass) this.outlinePass.selectedObjects = [];
				return;
			}

			mouse.set(position.x, position.y);
			const camera = this.camera;

			raycaster.setFromCamera(mouse, camera);
			raycaster.layers.enableAll();

			const intersects = raycaster.intersectObjects(this.scene.children, true);
			const target = intersects.filter(i => i.object.visible && i.object.name);
			this.hovered = target.length ? target[0].object : null;
			this.hoveredHit = firstFrontFacingHit(target, raycaster.ray.direction);
			if (this.outlinePass) this.outlinePass.selectedObjects = this.hovered ? [this.hovered] : [];
		});

		this.renderer.domElement.addEventListener('click', this.onClick);

		const outputPass = new OutputPass();
		this.composer.addPass(outputPass);

		// expose external size updater
		(window as any).threeViewer = this;
		(window as any).updateThreeViewerSize = (w?: number, h?: number) => this.updateSize(w, h);

		// initial size sync
		this.updateSize();

		this.animate();
	}

	public updatePixelRatio(fast?: boolean): void {
		if (this.setPixelRatioTimer) clearTimeout(this.setPixelRatioTimer);

		if (fast && !this.fullScreen) {
			(window as any).setPixelRatio = (window as any).devicePixelRatio;
			(store.status as any).renderMode = 'fast';

			this.setPixelRatioTimer = setTimeout(() => {
				this.updatePixelRatio();
				if (this.setPixelRatioTimer) clearTimeout(this.setPixelRatioTimer as any);
				(store.status as any).renderMode = 'render';
			}, 100);
		} else {
			(window as any).setPixelRatio = (window as any).devicePixelRatio;
		}

		const r = (window as any).setPixelRatio;
		if (this.fxaa) {
			this.composer.removePass(this.fxaa);
			this.fxaa.material.uniforms['resolution'].value.set(
				1 / (this.element.clientWidth * r),
				1 / (this.element.clientHeight * r)
			);
			this.composer.addPass(this.fxaa);
		}
		this.composer.setSize(this.element.clientWidth, this.element.clientHeight);
	}

	// NEW: external-callable universal size updater; also used by window resize
	public updateSize(w?: number, h?: number): void {

		//const element = document.querySelector("#section-3d-plugin")

		const width = w || this.element?.clientWidth || 100
		const height = h || this.element?.clientHeight || 100

		this.clientWidth = width;
		this.clientHeight = height;

		if (this.camera) {
			this.camera.aspect = width / height;
			this.camera.updateProjectionMatrix();
		}

		if (this.renderer) {
			this.renderer.setSize(width, height);
		}

		const r = this.renderer?.getPixelRatio?.() ?? (window as any).devicePixelRatio;
		if (this.composer) this.composer.setSize(width, height);
		if (this.outlinePass) this.outlinePass.setSize(width, height);
		if (this.fxaa) this.fxaa.material.uniforms['resolution'].value.set(1 / (width * r), 1 / (height * r));
	}

	// Backward-compat alias if some legacy code calls it
	//private setWindowResize(): void { this.updateSize(); }

	public setUpCameraControls(): void {
		this.controls = this.controls || new CameraControls(this.camera, this.renderer.domElement);

		this.controls.minPolarAngle = Math.PI * 0.2;
		this.controls.maxPolarAngle = Math.PI * 0.5;
		this.controls.minDistance = 0.4;
		this.controls.maxDistance = 1.5;

		this.controls.azimuthRotateSpeed = 2;
		this.controls.polarRotateSpeed = 2;
		this.controls.dollySpeed = 2;
		this.controls.draggingSmoothTime = 0;

		this.controls.mouseButtons.wheel = CameraControls.ACTION.NONE;
		this.controls.mouseButtons.left = CameraControls.ACTION.ROTATE;
		this.controls.mouseButtons.right = CameraControls.ACTION.OFFSET;

		// --- camera-move trace -------------------------------------------------
		// Something re-aims the camera after a section is framed — proven by the section
		// landing at NDC x 2.2 when it was placed dead centre. Reading the code has not found
		// it, so catch it in the act: every setLookAt and setTarget, with its call site.
		const camTrace: any[] = [];
		const rawSetLookAt = this.controls.setLookAt.bind(this.controls);
		const rawSetTarget = this.controls.setTarget.bind(this.controls);
		const recordMove = (kind: string, args: any[]) => {
			const site = (new Error().stack || '')
				.split('\n').slice(2, 6)
				.map((l) => l.trim().replace(/^at\s+/, ''))
				.join('  <-  ');
			camTrace.push({
				t: Math.round(performance.now()),
				kind,
				args: args.slice(0, 6).map((n) => Number(n).toFixed(3)).join(','),
				site,
			});
			if (camTrace.length > 40) camTrace.shift();
		};
		(this.controls as any).setLookAt = (...a: any[]) => { recordMove('setLookAt', a); return rawSetLookAt(...(a as [any, any, any, any, any, any])); };
		(this.controls as any).setTarget = (...a: any[]) => { recordMove('setTarget', a); return rawSetTarget(...(a as [any, any, any])); };
		(this as any).cameraTrace = () => { console.table(camTrace); return camTrace; };
	}

	private render(): void {
		const delta = clock.getDelta();
		this.controls.update(delta);
		this.composer.render();
		this.updateSize()
	}

	private animate(): void {
		this.animationFrameId = requestAnimationFrame(this.animate.bind(this));

		if (this.scene && this.camera) {
			this.animatePerformDepthRendering();
			this.animateResetAndRenderScene();
		}
	}

	private animatePerformDepthRendering(): void {
		this.renderer.setClearAlpha(0);
		this.scene.overrideMaterial = null;
	}

	private animateResetAndRenderScene(): void {
		this.renderer.setRenderTarget(null);
		this.render();
	}
}

export { ThreeViewer };

/**
 * Nearest intersection whose face turns back toward the ray.
 *
 * Product GLBs are routinely authored double-sided — every material in a Sketchfab export
 * is — so the nearest hit can be the INSIDE of a far wall. Only faces turned toward the
 * ray are somewhere a section could actually be engraved.
 */
function firstFrontFacingHit(
	hits: THREE.Intersection[],
	direction: THREE.Vector3
): { object: THREE.Object3D; point: THREE.Vector3; normal: THREE.Vector3 } | null {
	for (const hit of hits) {
		if (!hit.face) continue;
		const normal = hit.face.normal.clone().transformDirection(hit.object.matrixWorld).normalize();
		if (normal.dot(direction) >= 0) continue;
		return { object: hit.object, point: hit.point.clone(), normal };
	}
	return null;
}
